import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import { Textarea } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import CampaignService from "@/services/campaignService";

import ProductSelector from "@/components/campaigns/productSelector";
import FormFieldBuilder from "@/components/productActivation/formFieldBuilder";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

import { useCampaignFormFields } from "@/hooks/useCampaignFormFields";

import * as z from "zod";

import { formatDateTime } from "@/lib/dateUtils";

import CheckRole from "@/components/shared/checkRole";

const formFieldSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional(),
  type: z.string().optional(),
  required: z.boolean().optional(),
  options: z.array(z.string()).optional(),
  order: z.number().optional(),
});

const CampaignCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const formSchema = z.object({
    name: z.string().min(1, { message: t("common.validateMsg.requiredField") }),
    description: z.string().optional(),
    startDate: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    endDate: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    extendedWarrantyDurationMonths: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(1, { message: t("common.validateMsg.requiredField") }),
    formFields: z.array(formFieldSchema).optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      extendedWarrantyDurationMonths: 12,
      formFields: [],
    },
  });

  const { fields, addFormField, removeField, updateFieldOrder } =
    useCampaignFormFields(form);

  const onSubmit = async (data) => {
    if (selectedProducts.length === 0) {
      toast({
        title: t("common.error"),
        description: t("campaignsPage.selectProductsError"),
        variant: "error",
      });
      return;
    }

    try {
      setLoading(true);

      // Transform form fields to backend format
      const formFields = (data.formFields || []).map((field, index) => ({
        question: field.label,
        type: field.type.toUpperCase(),
        required: field.required || false,
        options: field.options || [],
        displayOrder: field.order ?? index,
      }));

      const payload = {
        name: data.name,
        description: data.description || "",
        extendedWarrantyDurationMonths: data.extendedWarrantyDurationMonths,
        startDate: formatDateTime(data.startDate),
        endDate: formatDateTime(data.endDate),
        products: selectedProducts.map((productId) => ({
          merchantProductId: productId,
        })),
        formFields,
      };
      const response = await CampaignService.createCampaign(payload);

      toast({
        title: t("common.success"),
        description: t("campaignsPage.createSuccess"),
        variant: "success",
      });

      navigate(`/campaigns/${response?.data?.id || response?.id}`);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        t("campaignsPage.createError");
      toast({
        title: t("common.error"),
        description: errorMessage,
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/campaigns">
          {t("campaignsPage.title")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("campaignsPage.createCampaign")}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="mx-auto max-w-4xl">
        <div className="mt-6">
          <h1 className="formTitle">{t("campaignsPage.createCampaign")}</h1>
          <p className="formDescription">
            {t("campaignsPage.createCampaignDesc")}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 space-y-6"
          >
            {/* Basic Information */}
            <div className="formBox">
              <h2 className="mb-4 text-lg font-semibold text-[#101828]">
                {t("campaignsPage.campaignInfo")}
              </h2>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.campaignName")} *</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.campaignDescription")}</Label>
                      <FormControl>
                        <Textarea rows={4} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <Label>{t("campaignsPage.startDate")} *</Label>
                        <FormControl>
                          <Input type="datetime-local" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <Label>{t("campaignsPage.endDate")} *</Label>
                        <FormControl>
                          <Input type="datetime-local" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="extendedWarrantyDurationMonths"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.warrantyExtension")} *</Label>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Product Selection */}
            <div className="formBox">
              <h2 className="mb-4 text-lg font-semibold text-[#101828]">
                {t("campaignsPage.products")}
              </h2>
              <ProductSelector
                selectedProducts={selectedProducts}
                onProductsChange={setSelectedProducts}
              />
            </div>

            {/* Form Fields */}
            <div className="formBox">
              <FormFieldBuilder
                fields={fields}
                addFormField={addFormField}
                updateFieldOrder={updateFieldOrder}
                removeField={removeField}
                form={form}
              />
            </div>

            {/* Form Actions */}
            <CheckRole section="CAMPAIGNS" action="EDIT">
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="secondaryGray"
                  onClick={() => navigate("/campaigns")}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? t("common.saving") : t("common.save")}
                </Button>
              </div>
            </CheckRole>
          </form>
        </Form>
      </div>
    </main>
  );
};

export default CampaignCreatePage;
